import { useCallback, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { AddOrderItemPayload } from 'types/api/orders.type';

import { ordersApi } from 'redux/api/ordersApi';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { setOrderId, setOrderNumber } from 'redux/slices/settings';

import { ordersDataService } from 'services';

import { pickedAddresses } from 'components/OrderAddressBlocks';

type UseOrderModalOptions = {
  /** Product data to add after order creation. If omitted, modal just creates the order. */
  getItemPayload?: () => AddOrderItemPayload;
  /** Called after the item is successfully added (or order created if no payload). */
  onSuccess?: () => void;
};

const useOrderModal = (options?: UseOrderModalOptions) => {
  const { season, zone, warehouse, marketLine } = useAppSelector((state) => state.settings);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [isOrderModalVisible, isOrderModalVisibleSet] = useState<boolean>(false);
  const [isOrderLoading, isOrderLoadingSet] = useState<boolean>(false);

  const onOrderSubmit = useCallback(
    (data: FieldValues) => {
      if (data.customer) {
        isOrderLoadingSet(true);
        ordersDataService
          .post(season, zone, warehouse.code, data.customer, marketLine, pickedAddresses(data))
          .then((res) => {
            const newOrderId: string = res.data.orderId;
            const newOrderNumber = res.data.orderNumber;
            localStorage.setItem('orderNumber', String(newOrderNumber));
            localStorage.setItem('orderId', newOrderId);
            dispatch(setOrderId(newOrderId));
            dispatch(setOrderNumber(String(newOrderNumber)));
            dispatch(ordersApi.util.invalidateTags(['DropdownOrders', 'OrdersList']));

            const payload = options?.getItemPayload?.();
            if (payload) {
              ordersDataService
                .addItem(newOrderId, payload)
                .then(() => {
                  dispatch(
                    ordersApi.util.invalidateTags([{ type: 'OrderHeader', id: newOrderId }])
                  );
                  isOrderModalVisibleSet(false);
                  isOrderLoadingSet(false);
                  toast.success(t('product.addedSuccessfully'));
                  options?.onSuccess?.();
                })
                .catch(() => {
                  isOrderModalVisibleSet(false);
                  isOrderLoadingSet(false);
                  toast.error(t('product.errorAdding'));
                });
            } else {
              isOrderModalVisibleSet(false);
              isOrderLoadingSet(false);
              options?.onSuccess?.();
            }
          })
          .catch(() => {
            isOrderLoadingSet(false);
          });
      }
    },
    [season, zone, warehouse.code, marketLine, options?.getItemPayload, options?.onSuccess]
  );

  return {
    isOrderModalVisible,
    isOrderModalVisibleSet,
    isOrderLoading,
    onOrderSubmit,
  };
};

export { useOrderModal };
